type FileIconProp = {
  mimeType?: string;
  /**
   * File name (or path). Used as a fallback when the MIME type is generic or
   * ambiguous (e.g. Tika's `application/x-tika-msoffice` for a legacy `.xls`),
   * resolving the icon from the file extension instead.
   */
  fileName?: string;
  /** Wrapper sizing classes. Defaults to a fixed 33×33 box. */
  className?: string;
  /**
   * Use the flat, shadow-less icon variants (white chip, no drop shadow).
   * Scoped to the Drive share-link viewer; other surfaces keep the default
   * shadowed icons.
   */
  flat?: boolean;
};

/**
 * Picks the icon asset for a MIME type. Detection is by substring (not
 * `endsWith`) so format variants resolve correctly — e.g. macro-enabled
 * (`application/vnd.ms-excel.sheet.macroEnabled.12`) and template
 * (`…spreadsheetml.template`) Office files, the OpenDocument family, and
 * legacy `application/excel`/`x-msexcel` aliases, which an exact-suffix match
 * would drop to `unknown.svg`.
 *
 * Order matters: specific document/data formats are matched before the generic
 * top-level `type`, and spreadsheets/presentations are matched before Word —
 * every OOXML MIME contains the substring `officedocument`, so Word is keyed on
 * `wordprocessing`/`msword` specifically rather than a generic `document`.
 */
function iconFileForMime(mimeType?: string): string {
  if (!mimeType) return "unknown.svg";
  const mime = mimeType.toLowerCase();
  const [type, subType = ""] = mime.split("/");

  if (mime.includes("pdf")) return "pdf.svg";
  if (subType === "csv" || mime.includes("comma-separated")) return "csv.svg";

  // Spreadsheets: xls, xlsx, xlsm, xltx, ods, … (and legacy excel aliases).
  if (
    mime.includes("excel") ||
    mime.includes("spreadsheet") ||
    subType.endsWith("sheet")
  ) {
    return "excel.svg";
  }
  // Presentations: ppt, pptx, potx, odp, …
  if (
    mime.includes("powerpoint") ||
    mime.includes("presentation") ||
    mime.includes("slide")
  ) {
    return "ppt.svg";
  }
  // Word-processing documents: doc, docx, dotx, odt, rtf, …
  if (
    mime.includes("msword") ||
    mime.includes("wordprocessing") ||
    mime.endsWith("opendocument.text") ||
    subType === "rtf"
  ) {
    return "word.svg";
  }
  // Archives: zip, rar, 7z, tar, gzip, … (incl. x-zip-compressed).
  if (
    mime.includes("zip") ||
    mime.includes("compressed") ||
    mime.includes("archive") ||
    mime.includes("x-tar") ||
    mime.includes("gzip") ||
    mime.includes("rar") ||
    mime.includes("7z")
  ) {
    return "zip.svg";
  }

  if (type === "image") return "image.svg";
  if (type === "video") return "video.svg";
  if (type === "audio") return "audio.svg";
  if (type === "text") return "text.svg";

  return "unknown.svg";
}

/** Maps a file extension to an icon asset. */
const EXTENSION_ICONS: Record<string, string> = {
  // Spreadsheets
  xls: "excel.svg",
  xlsx: "excel.svg",
  xlsm: "excel.svg",
  xlsb: "excel.svg",
  xlt: "excel.svg",
  xltx: "excel.svg",
  ods: "excel.svg",
  // Word processing
  doc: "word.svg",
  docx: "word.svg",
  docm: "word.svg",
  dot: "word.svg",
  dotx: "word.svg",
  odt: "word.svg",
  rtf: "word.svg",
  // Presentations
  ppt: "ppt.svg",
  pptx: "ppt.svg",
  pptm: "ppt.svg",
  pot: "ppt.svg",
  potx: "ppt.svg",
  odp: "ppt.svg",
  // Documents / data
  pdf: "pdf.svg",
  csv: "csv.svg",
  // Archives
  zip: "zip.svg",
  rar: "zip.svg",
  "7z": "zip.svg",
  tar: "zip.svg",
  gz: "zip.svg",
  tgz: "zip.svg",
  bz2: "zip.svg",
  xz: "zip.svg",
  // Images
  png: "image.svg",
  jpg: "image.svg",
  jpeg: "image.svg",
  gif: "image.svg",
  webp: "image.svg",
  svg: "image.svg",
  bmp: "image.svg",
  tif: "image.svg",
  tiff: "image.svg",
  heic: "image.svg",
  ico: "image.svg",
  // Video
  mp4: "video.svg",
  mov: "video.svg",
  avi: "video.svg",
  mkv: "video.svg",
  webm: "video.svg",
  wmv: "video.svg",
  flv: "video.svg",
  m4v: "video.svg",
  // Audio
  mp3: "audio.svg",
  wav: "audio.svg",
  ogg: "audio.svg",
  flac: "audio.svg",
  aac: "audio.svg",
  m4a: "audio.svg",
  wma: "audio.svg",
  // Text / code
  txt: "text.svg",
  log: "text.svg",
  md: "text.svg",
  json: "text.svg",
  xml: "text.svg",
  html: "text.svg",
  htm: "text.svg",
  css: "text.svg",
  js: "text.svg",
  ts: "text.svg",
  yml: "text.svg",
  yaml: "text.svg",
};

/** Picks the icon asset from a file name's extension (unknown if none/unmapped). */
function iconFileForExtension(fileName?: string): string {
  if (!fileName) return "unknown.svg";
  const dot = fileName.lastIndexOf(".");
  if (dot < 0 || dot === fileName.length - 1) return "unknown.svg";
  const ext = fileName.slice(dot + 1).toLowerCase();
  return EXTENSION_ICONS[ext] ?? "unknown.svg";
}

/**
 * Resolves the icon asset for a file, preferring the MIME type and falling back
 * to the file extension when the MIME is missing, generic or ambiguous (e.g.
 * Tika's `application/x-tika-msoffice` / `application/octet-stream`).
 *
 * Exported as the file-type category the user actually sees: two files that
 * render the same icon (a `.doc` and a `.docx`) resolve to the same asset, which
 * is what the list's type sort groups by (see `fileSort`).
 */
export function fileIconAsset(mimeType?: string, fileName?: string): string {
  const byMime = iconFileForMime(mimeType);
  return byMime !== "unknown.svg" ? byMime : iconFileForExtension(fileName);
}

export const FileIcon = ({
  mimeType,
  fileName,
  className,
  flat,
}: FileIconProp) => {
  const iconFile = fileIconAsset(mimeType, fileName);

  const basePath = flat ? "/assets/fileIcons/flat" : "/assets/fileIcons";

  return (
    <div className={className ?? "h-[33px] w-[33px]"}>
      <img
        src={`${basePath}/${iconFile}`}
        alt={iconFile}
        aria-hidden
        draggable={false}
        className="h-full w-full object-contain"
      />
    </div>
  );
};
